/**
 * LiveRoomsPage — the Live Rooms tab with Nova co-host baked in
 * In-tab experience: list of rooms, click Join to enter room view
 */

import React, { useState } from 'react';
import { NovaLiveRoomCoHost } from './NovaLiveRoomCoHost';
import { getChapterForRoom, getSpiralRiskForRoom } from '../data/novaCommunityConfig';
import { CHAPTER_THEMES as CHAPTER_THEMES_DATA } from '../data/chapterThemes';

function hexWithAlpha(hex, alpha) {
  if (!hex || !String(hex).startsWith('#')) return hex;
  const a = Math.max(0, Math.min(1, alpha));
  const hexA = Math.round(a * 255).toString(16).padStart(2, '0');
  return `${hex}${hexA}`;
}

const MOCK_MESSAGES_BY_ROOM = {
  'live-beta': [
    { id: '1', author: 'Sarah K.', initial: 'SK', text: "My clinic said they'll call by noon...", isOra: false },
    { id: '2', author: 'Jamie', initial: 'J', text: 'Anyone else checking their phone every 30 seconds 😅', isOra: false },
  ],
  'live-fc-tracking': [
    { id: '1', author: 'Jasmine', initial: 'J', text: 'Is it weird that my cycle is 32 days one month and 26 the next??', isOra: false },
    { id: '2', author: 'Riley', initial: 'R', text: 'Nope — first years are often irregular. Nova said that’s super common.', isOra: false },
  ],
  'live-fc-mood': [
    { id: '1', author: 'Aisha', initial: 'A', text: 'I snap at everyone 3 days before my period and then feel awful after 😔', isOra: false },
    { id: '2', author: 'Maya', initial: 'M', text: 'Same. Tracking it helped me warn my friends — sounds silly but it helped.', isOra: false },
  ],
  default: [
    { id: '1', author: 'Maya', initial: 'M', text: 'Did anyone else feel super bloated on stim day 8?', isOra: false },
    { id: '2', author: 'Sarah K.', initial: 'SK', text: 'Yes! Mine peaked around day 9. Heating pad helped a lot.', isOra: false },
    { id: '3', author: 'Priya', initial: 'P', text: 'Same here. Also drinking electrolyte mix — game changer.', isOra: false },
  ],
};

/** LiveRoomView — the inside of a room with Nova co-hosting */
function LiveRoomView({ theme, chapter, room, onBack, onOpenNova }) {
  const [message, setMessage] = useState('');
  const seedMessages = MOCK_MESSAGES_BY_ROOM[room.id] || MOCK_MESSAGES_BY_ROOM.default;
  const [messages, setMessages] = useState(seedMessages);

  const t = theme || CHAPTER_THEMES_DATA[chapter] || CHAPTER_THEMES_DATA.fertility;

  const sendMessage = () => {
    if (!message.trim()) return;
    setMessages((prev) => [
      ...prev,
      { id: Date.now().toString(), author: 'Maya', initial: 'M', text: message.trim(), isOra: false },
    ]);
    setMessage('');
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: 400, background: 'var(--white)', borderRadius: 14, border: '1px solid var(--border)', overflow: 'hidden' }}>
      {/* Room header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', borderBottom: '1px solid var(--border)' }}>
        <button
          type="button"
          onClick={onBack}
          style={{ background: 'none', border: 'none', padding: 0, fontSize: 14, color: 'rgba(60,30,45,0.5)', cursor: 'pointer', fontFamily: 'inherit' }}
          onMouseEnter={(e) => { e.currentTarget.style.color = '#2d1a22'; }}
          onMouseLeave={(e) => { e.currentTarget.style.color = 'rgba(60,30,45,0.5)'; }}
        >
          ← Back
        </button>
        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={{ fontSize: 13, fontWeight: 600, color: '#2d1a22', margin: 0 }}>{room.topic || room.name}</p>
          <p style={{ fontSize: 11, color: 'rgba(60,30,45,0.5)', margin: 0 }}>
            {room.online ?? room.onlineCount ?? 0} online · Nova is here
          </p>
        </div>
      </div>

      <NovaLiveRoomCoHost
        theme={t}
        chapter={chapter}
        roomName={room.topic || room.name}
        spiralRisk={room.spiralRisk}
        onOpenNova={onOpenNova}
      />

      {/* Message feed */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: 12, minHeight: 120 }}>
        {messages.length === 0 && (
          <p style={{ fontSize: 12, color: 'rgba(60,30,45,0.5)', textAlign: 'center', padding: 24, margin: 0 }}>
            Be the first to share something in this room.
          </p>
        )}
        {messages.map((msg) => (
          <div
            key={msg.id}
            style={{
              display: 'flex',
              gap: 8,
              alignItems: 'flex-start',
              ...(msg.isOra && { background: `linear-gradient(140deg, ${t.accentBg}, #f5f0f5)`, borderRadius: 12, padding: 10 }),
            }}
          >
            <div
              style={{
                width: 28,
                height: 28,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 11,
                fontWeight: 600,
                flexShrink: 0,
                background: msg.isOra ? '#D4537E' : t.accentBg,
                color: msg.isOra ? 'white' : t.accent,
              }}
            >
              {msg.initial}
            </div>
            <div>
              <span style={{ fontSize: 11, fontWeight: 600, color: msg.isOra ? t.accentText : '#2d1a22' }}>{msg.author}</span>
              <p style={{ fontSize: 13, color: '#2d1a22', lineHeight: 1.5, margin: 0 }}>{msg.text}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Input */}
      <div style={{ borderTop: '1px solid var(--border)', padding: '12px 16px' }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <input
            type="text"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Share something..."
            style={{
              flex: 1,
              background: 'var(--color-background-secondary)',
              borderRadius: 10,
              padding: '10px 14px',
              fontSize: 13,
              color: '#2d1a22',
              border: '1px solid var(--border)',
              outline: 'none',
              fontFamily: 'inherit',
            }}
            onKeyDown={(e) => e.key === 'Enter' && sendMessage()}
          />
          <button
            type="button"
            onClick={sendMessage}
            style={{
              width: 36,
              height: 36,
              borderRadius: '50%',
              background: t.accent,
              color: 'white',
              border: 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              flexShrink: 0,
            }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M5 12h14M12 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
}

/** LiveRoomsPage — list + room view */
export function LiveRoomsPage({ theme, chapter = 'fertility', rooms, onJoinRoom, onOpenNova }) {
  const [activeRoom, setActiveRoom] = useState(null);
  const baseTheme = theme || CHAPTER_THEMES_DATA[chapter] || CHAPTER_THEMES_DATA.fertility;
  const t = { ...baseTheme, cardGradient: `linear-gradient(140deg, ${baseTheme.accentBg}, #f5f0f5)` };

  if (activeRoom) {
    const roomChapter = getChapterForRoom(activeRoom) || chapter;
    return (
      <LiveRoomView
        theme={CHAPTER_THEMES_DATA[roomChapter] || baseTheme}
        chapter={roomChapter}
        room={activeRoom}
        onBack={() => setActiveRoom(null)}
        onOpenNova={onOpenNova}
      />
    );
  }

  return (
    <div>
      <div
        style={{
          borderRadius: 14,
          background: t.cardGradient,
          padding: '16px 20px',
          marginBottom: 16,
          border: `0.5px solid ${hexWithAlpha(t.accent, 0.12)}`,
        }}
      >
        <p style={{ fontSize: 10, fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'rgba(60,30,45,0.5)', marginBottom: 4 }}>
          LIVE CHAT ROOMS
        </p>
        <p style={{ fontSize: 18, fontWeight: 600, color: '#2d1a22', marginBottom: 4 }}>Live chat rooms</p>
        <p style={{ fontSize: 12, color: 'rgba(60,30,45,0.5)', margin: 0 }}>
          Immediate answers from women going through the same thing right now
        </p>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {rooms.map((room) => {
          const r = typeof room.topic !== 'undefined' ? room : { ...room, topic: room.name, desc: room.description };
          const onlineCount = r.online ?? r.onlineCount ?? 0;
          const status = onlineCount === 0 ? 'empty' : onlineCount <= 3 ? 'quiet' : 'active';
          const spiralRisk = r.spiralRisk ?? getSpiralRiskForRoom({ topic: r.topic });

          return (
            <div
              key={r.id || r.topic}
              style={{
                background: 'var(--white)',
                borderRadius: 14,
                border: '1px solid var(--border)',
                padding: '16px 20px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    width: 10,
                    height: 10,
                    borderRadius: '50%',
                    flexShrink: 0,
                    background: status === 'active' ? '#1D9E75' : status === 'quiet' ? '#D85A30' : 'var(--color-text-tertiary)',
                  }}
                />
                <div style={{ minWidth: 0 }}>
                  <p style={{ fontSize: 13, fontWeight: 600, color: '#2d1a22', margin: 0 }}>{r.topic || r.name}</p>
                  <p style={{ fontSize: 12, color: 'rgba(60,30,45,0.5)', margin: '2px 0 0 0' }}>{r.desc || r.description}</p>
                  <p style={{ fontSize: 10, fontWeight: 500, color: t.accentText, margin: '4px 0 0 0' }}>Nova is in this room</p>
                </div>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0 }}>
                {onlineCount > 0 ? (
                  <>
                    <span style={{ fontSize: 11, color: 'rgba(60,30,45,0.5)' }}>{onlineCount} online</span>
                    <button
                      type="button"
                      onClick={() => {
                        setActiveRoom({ ...r, spiralRisk });
                        onJoinRoom?.(r.id || r.topic);
                      }}
                      style={{
                        fontSize: 11,
                        fontWeight: 500,
                        padding: '6px 14px',
                        borderRadius: 999,
                        border: `0.5px solid ${hexWithAlpha(t.accent, 0.35)}`,
                        color: t.accentText,
                        background: 'transparent',
                        cursor: 'pointer',
                        fontFamily: 'inherit',
                      }}
                    >
                      Join
                    </button>
                  </>
                ) : (
                  <button
                    type="button"
                    onClick={() => {
                      setActiveRoom({ ...r, spiralRisk });
                      onJoinRoom?.(r.id || r.topic);
                    }}
                    style={{
                      fontSize: 11,
                      fontWeight: 500,
                      background: 'none',
                      border: 'none',
                      padding: 0,
                      color: t.accentText,
                      cursor: 'pointer',
                      fontFamily: 'inherit',
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.textDecoration = 'underline'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.textDecoration = 'none'; }}
                  >
                    Start room
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default LiveRoomsPage;
